/**
 * Google Geocoding & Places Service
 *
 * - searchGoogleGeocoding: forward geocoding for auto-resolve (no coordinates)
 * - autocompletePlaces: suggestions while user types in AddressAutocomplete
 * - getPlaceDetails: coordinates + address components for a selected place
 *
 * Uses Google Geocoding API + Places API (New) — both CORS-safe from browser.
 */

const GOOGLE_GEOCODING_URL = 'https://maps.googleapis.com/maps/api/geocode/json';
const GOOGLE_PLACES_AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete';
const GOOGLE_PLACES_DETAILS_URL = 'https://places.googleapis.com/v1/places';

export interface GoogleGeocodingResult {
  lat: number;
  lng: number;
  formattedAddress: string;
  street: string;
  zip: string;
  locality: string;
  country: string;
  countryCode: string;
  region: string;
}

function getComponent(
  components: Array<{ types: string[]; long_name: string; short_name: string }>,
  type: string,
  short = false
): string {
  const c = components.find(comp => comp.types.includes(type));
  return c ? (short ? c.short_name : c.long_name) : '';
}

/**
 * Forward geocode a query string via Google Geocoding API.
 * Returns the best result or null if nothing found / API key missing.
 */
export async function searchGoogleGeocoding(
  query: string
): Promise<GoogleGeocodingResult | null> {
  const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;
  if (!apiKey) {
    console.warn('[googleGeocoding] VITE_GOOGLE_PLACES_API_KEY not configured');
    return null;
  }

  try {
    const params = new URLSearchParams({
      address: query,
      key: apiKey,
      language: 'de',
    });

    const response = await fetch(`${GOOGLE_GEOCODING_URL}?${params}`);
    if (!response.ok) return null;

    const data = await response.json();
    if (data.status !== 'OK' || !data.results?.length) return null;

    const result = data.results[0];
    const components = result.address_components ?? [];
    const loc = result.geometry?.location;
    if (!loc) return null;

    const streetNumber = getComponent(components, 'street_number');
    const route = getComponent(components, 'route');
    const street = [route, streetNumber].filter(Boolean).join(' ');

    return {
      lat: loc.lat,
      lng: loc.lng,
      formattedAddress: result.formatted_address,
      street,
      zip: getComponent(components, 'postal_code'),
      locality:
        getComponent(components, 'locality') ||
        getComponent(components, 'administrative_area_level_1'),
      country: getComponent(components, 'country'),
      countryCode: getComponent(components, 'country', true),
      region: getComponent(components, 'administrative_area_level_1'),
    };
  } catch {
    return null;
  }
}

export interface GooglePlacePrediction {
  placeId: string;
  primaryText: string;
  secondaryText: string;
  fullText: string;
}

/**
 * Autocomplete suggestions via Google Places API (New).
 * Returns up to 5 predictions for the given input string.
 */
export async function autocompletePlaces(
  input: string
): Promise<GooglePlacePrediction[]> {
  const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;
  if (!apiKey || input.length < 2) return [];

  try {
    const response = await fetch(GOOGLE_PLACES_AUTOCOMPLETE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': apiKey,
      },
      body: JSON.stringify({ input, languageCode: 'de' }),
    });
    if (!response.ok) return [];

    const data = await response.json();
    return (data.suggestions ?? []).map(
      (s: {
        placePrediction: {
          placeId: string;
          structuredFormat: { mainText: { text: string }; secondaryText?: { text: string } };
          text: { text: string };
        };
      }): GooglePlacePrediction => ({
        placeId: s.placePrediction.placeId,
        primaryText: s.placePrediction.structuredFormat.mainText.text,
        secondaryText: s.placePrediction.structuredFormat.secondaryText?.text ?? '',
        fullText: s.placePrediction.text.text,
      })
    );
  } catch {
    return [];
  }
}

/**
 * Fetch coordinates + address components for a selected place via Places API (New).
 */
export async function getPlaceDetails(
  placeId: string
): Promise<GoogleGeocodingResult | null> {
  const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;
  if (!apiKey) return null;

  try {
    const fields = 'id,displayName,formattedAddress,location,addressComponents';
    const response = await fetch(
      `${GOOGLE_PLACES_DETAILS_URL}/${placeId}?fields=${fields}&languageCode=de`,
      { headers: { 'X-Goog-Api-Key': apiKey } }
    );
    if (!response.ok) return null;

    const place = await response.json();
    const loc = place.location;
    if (!loc) return null;

    const components: Array<{ longText: string; shortText: string; types: string[] }> =
      place.addressComponents ?? [];

    const getComp = (type: string, short = false) => {
      const c = components.find(comp => comp.types.includes(type));
      return c ? (short ? c.shortText : c.longText) : '';
    };

    const route = getComp('route');
    const streetNumber = getComp('street_number');
    const street = [route, streetNumber].filter(Boolean).join(' ');

    return {
      lat: loc.latitude,
      lng: loc.longitude,
      formattedAddress: place.formattedAddress ?? '',
      street,
      zip: getComp('postal_code'),
      locality: getComp('locality') || getComp('administrative_area_level_1'),
      country: getComp('country'),
      countryCode: getComp('country', true),
      region: getComp('administrative_area_level_1'),
    };
  } catch {
    return null;
  }
}
